import { db } from '../firebase';
import { collection, query, where, getDocs, addDoc, updateDoc, doc, deleteDoc, getDoc } from 'firebase/firestore';
import axios from 'axios';

// Collection names
const TEMPLATES_COLLECTION = 'templates';
const WORKFLOWS_COLLECTION = 'workflows';

// Template Management
export const getTemplatesByUserId = async (userId) => {
  try {
    const templatesRef = collection(db, TEMPLATES_COLLECTION);
    const q = query(templatesRef, where("userId", "==", userId));
    const querySnapshot = await getDocs(q);
    
    const templates = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      
      // Process the data to ensure boolean values are properly handled
      const processedData = {
        ...data,
        // Ensure boolean values are properly set as booleans
        sidebarOnly: Boolean(data.sidebarOnly),
        useFeaturedImage: Boolean(data.useFeaturedImage)
      };
      
      templates.push({
        id: doc.id,
        ...processedData
      });
    });
    
    return templates;
  } catch (error) {
    console.error('Error loading templates:', error);
    throw error;
  }
};

export const getTemplateById = async (templateId) => {
  try {
    if (!templateId) throw new Error('Template ID is required');
    
    const templateRef = doc(db, TEMPLATES_COLLECTION, templateId);
    const templateDoc = await getDoc(templateRef);
    
    if (!templateDoc.exists()) {
      console.error(`Template not found with ID: ${templateId}`);
      return null;
    }
    
    const data = templateDoc.data();
    
    // Process the data to ensure boolean values are properly handled
    const processedData = {
      ...data,
      // Ensure boolean values are properly set as booleans
      sidebarOnly: Boolean(data.sidebarOnly),
      useFeaturedImage: Boolean(data.useFeaturedImage)
    };
    
    return {
      id: templateDoc.id,
      ...processedData
    };
  } catch (error) {
    console.error(`Error getting template with ID ${templateId}:`, error);
    throw error;
  }
};

export const getWorkflowsByUserId = async (userId) => {
  try {
    const workflowsRef = collection(db, WORKFLOWS_COLLECTION);
    const q = query(workflowsRef, where("userId", "==", userId));
    const querySnapshot = await getDocs(q);
    
    const workflows = [];
    querySnapshot.forEach((doc) => {
      workflows.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return workflows;
  } catch (error) {
    console.error('Error loading workflows:', error);
    throw error;
  }
};

export const saveTemplate = async (userId, templateData) => {
  try {
    if (!userId) throw new Error('User ID is required');
    if (!templateData) throw new Error('Template data is required');
    if (!templateData.name) throw new Error('Template name is required');
    if (!templateData.prompt) throw new Error('Template prompt is required');

    // Log the original template data received
    console.log('TEMPLATE SERVICE - Original new template data received:', {
      type: templateData.type,
      useFeaturedImage: templateData.useFeaturedImage,
      sidebarOnly: templateData.sidebarOnly,
      outputModes: templateData.outputModes,
      outputMetafieldModes: templateData.outputMetafieldModes
    });
    
    // Create a clean template data object - IMPORTANT: Do not spread templateData as it might contain undefined values
    const newTemplateData = {
      // Basic properties
      name: templateData.name,
      prompt: templateData.prompt,
      userId,
      createdAt: new Date().toISOString(),
      // Keep the exact template type that was passed in
      type: templateData.type,
      target: templateData.target || 'description',
      metafields: templateData.metafields || [],
      // Arrays
      inputFields: templateData.inputFields || [],
      outputFields: templateData.outputFields || [],
      inputMetafields: templateData.inputMetafields || [],
      outputMetafields: templateData.outputMetafields || [],
      // Model information
      modelId: templateData.modelId || '',
      creditCostPerOutput: templateData.creditCostPerOutput || 1,
      // Image template specific properties - FORCE boolean values
      useFeaturedImage: templateData.useFeaturedImage === true,
      sidebarOnly: templateData.sidebarOnly === true,
      // Objects - create fresh copies to avoid reference issues
      outputModes: {...(templateData.outputModes || {})},
      outputMetafieldModes: {...(templateData.outputMetafieldModes || {})}
    };
    
    // Log the final data being saved to Firebase
    console.log('TEMPLATE SERVICE - Final new template data to save:', {
      useFeaturedImage: newTemplateData.useFeaturedImage,
      sidebarOnly: newTemplateData.sidebarOnly,
      outputModes: newTemplateData.outputModes,
      outputMetafieldModes: newTemplateData.outputMetafieldModes
    });

    // Log the template data being saved
    console.log('Saving template with model info:', {
      modelId: newTemplateData.modelId,
      creditCostPerOutput: newTemplateData.creditCostPerOutput
    });

    const docRef = await addDoc(collection(db, TEMPLATES_COLLECTION), newTemplateData);
    
    return {
      id: docRef.id,
      ...newTemplateData
    };
  } catch (error) {
    console.error('Error saving template:', error);
    throw error;
  }
};

export const saveWorkflow = async (userId, workflowData) => {
  try {
    if (!userId) throw new Error('User ID is required');
    if (!workflowData) throw new Error('Workflow data is required');
    if (!workflowData.name) throw new Error('Workflow name is required');
    if (!workflowData.templates?.length) throw new Error('Workflow must include at least one template');

    const newWorkflowData = {
      ...workflowData,
      userId,
      createdAt: new Date().toISOString(),
      templates: workflowData.templates || []
    };

    const docRef = await addDoc(collection(db, WORKFLOWS_COLLECTION), newWorkflowData);
    
    return {
      id: docRef.id,
      ...newWorkflowData
    };
  } catch (error) {
    console.error('Error saving workflow:', error);
    throw error;
  }
};

export const updateTemplate = async (templateId, templateData) => {
  try {
    if (!templateId) throw new Error('Template ID is required');
    if (!templateData) throw new Error('Template data is required');
    if (!templateData.name) throw new Error('Template name is required');
    if (!templateData.prompt) throw new Error('Template prompt is required');
    
    // First get the existing template to preserve userId
    const existingTemplateRef = doc(db, TEMPLATES_COLLECTION, templateId);
    const templateSnap = await getDoc(existingTemplateRef);
    
    if (!templateSnap.exists()) {
      throw new Error('Template not found');
    }
    
    // Get the existing template data, especially the userId
    const existingTemplate = templateSnap.data();

    // Log the original template data received
    console.log('TEMPLATE SERVICE - Original template data received:', {
      type: templateData.type,
      useFeaturedImage: templateData.useFeaturedImage,
      sidebarOnly: templateData.sidebarOnly,
      outputModes: templateData.outputModes,
      outputMetafieldModes: templateData.outputMetafieldModes
    });
    
    // Prepare update data - IMPORTANT: Do not spread templateData as it might contain undefined values
    const updateData = {
      // CRITICAL: Preserve the userId from the existing template
      userId: existingTemplate.userId,
      // Basic properties
      name: templateData.name,
      prompt: templateData.prompt,
      updatedAt: new Date().toISOString(),
      // Keep the exact template type that was passed in
      type: templateData.type,
      target: templateData.target,
      metafield: templateData.metafield,
      // Arrays
      inputFields: templateData.inputFields || [],
      outputFields: templateData.outputFields || [],
      inputMetafields: templateData.inputMetafields || [],
      outputMetafields: templateData.outputMetafields || [],
      // Model information
      modelId: templateData.modelId || '',
      creditCostPerOutput: templateData.creditCostPerOutput || 1,
      // Image template specific properties - FORCE boolean values
      useFeaturedImage: templateData.useFeaturedImage === true,
      sidebarOnly: templateData.sidebarOnly === true,
      // Objects - create fresh copies to avoid reference issues
      outputModes: {...(templateData.outputModes || {})},
      outputMetafieldModes: {...(templateData.outputMetafieldModes || {})}
    };
    
    // Log the final data being saved to Firebase
    console.log('TEMPLATE SERVICE - Final template data to save:', {
      userId: updateData.userId, // Log userId to verify it's included
      useFeaturedImage: updateData.useFeaturedImage,
      sidebarOnly: updateData.sidebarOnly,
      outputModes: updateData.outputModes,
      outputMetafieldModes: updateData.outputMetafieldModes
    });

    // Log the template data being updated
    console.log('Updating template with model info:', {
      templateId,
      modelId: updateData.modelId,
      creditCostPerOutput: updateData.creditCostPerOutput
    });

    // Use the same reference to update the document
    await updateDoc(existingTemplateRef, updateData);

    return {
      id: templateId,
      ...updateData
    };
  } catch (error) {
    console.error('Error updating template:', error);
    throw error;
  }
};

export const deleteTemplate = async (templateId) => {
  try {
    if (!templateId) throw new Error('Template ID is required');
    
    const templateRef = doc(db, TEMPLATES_COLLECTION, templateId);
    await deleteDoc(templateRef);
  } catch (error) {
    console.error('Error deleting template:', error);
    throw error;
  }
};

export const deleteWorkflow = async (workflowId) => {
  try {
    if (!workflowId) throw new Error('Workflow ID is required');
    
    const workflowRef = doc(db, WORKFLOWS_COLLECTION, workflowId);
    await deleteDoc(workflowRef);
  } catch (error) {
    console.error('Error deleting workflow:', error);
    throw error;
  }
};

export const updateWorkflow = async (workflowId, workflowData) => {
  try {
    if (!workflowId) throw new Error('Workflow ID is required');
    if (!workflowData) throw new Error('Workflow data is required');
    if (!workflowData.name) throw new Error('Workflow name is required');
    if (!workflowData.templates?.length) throw new Error('Workflow must include at least one template');

    const workflowRef = doc(db, WORKFLOWS_COLLECTION, workflowId);
    await updateDoc(workflowRef, {
      ...workflowData,
      updatedAt: new Date().toISOString()
    });

    return {
      id: workflowId,
      ...workflowData
    };
  } catch (error) {
    console.error('Error updating workflow:', error);
    throw error;
  }
};

export const applyTemplateResult = (productId, template, result) => {
  const updates = {};
  
  // Handle regular fields
  if (template.outputFields?.length > 0) {
    template.outputFields.forEach(field => {
      // Special handling for alt_text - the only image alt text field we should use
      if (field === 'alt_text') {
        updates.image_alt_text = result.alt_text?.toString().trim() ?? '';
        console.log('Template service: Setting image alt text to:', updates.image_alt_text);
      }
      // Special handling for handle field
      else if (field === 'handle') {
        updates.handle = result.handle?.toString().trim() ?? '';
        console.log('Template service: Setting handle to:', updates.handle);
      }
      // Special handling for tags field
      else if (field === 'tags') {
        updates.tags = result.tags?.toString().trim() ?? '';
        console.log('Template service: Setting tags to:', updates.tags);
      }
      // Special handling for productType field
      else if (field === 'product_type') {
        updates.product_type = result.productType?.toString().trim() ?? '';
        console.log('Template service: Setting product_type to:', updates.product_type);
      }
      // For all other fields
      else if (result[field] !== undefined) {
        updates[field] = result[field];
      }
    });
  }

  console.log('Final updates object:', updates);
  return updates;
};

export const duplicateTemplate = async (templateId, userId) => {
  try {
    // Get the original template
    const templateDoc = await db.collection('templates').doc(templateId).get();
    if (!templateDoc.exists) {
      throw new Error('Template not found');
    }

    const templateData = templateDoc.data();
    
    // Create a new template with the same data but a new name
    const newTemplateData = {
      ...templateData,
      name: `${templateData.name} (Copy)`,
      userId,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    // Add the new template
    const newTemplateRef = await db.collection('templates').add(newTemplateData);
    return { id: newTemplateRef.id, ...newTemplateData };
  } catch (error) {
    console.error('Error duplicating template:', error);
    throw error;
  }
};

export const duplicateWorkflow = async (workflowId, userId) => {
  try {
    // Get the original workflow
    const workflowDoc = await db.collection('workflows').doc(workflowId).get();
    if (!workflowDoc.exists) {
      throw new Error('Workflow not found');
    }

    const workflowData = workflowDoc.data();
    
    // Create a new workflow with the same data but a new name
    const newWorkflowData = {
      ...workflowData,
      name: `${workflowData.name} (Copy)`,
      userId,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    // Add the new workflow
    const newWorkflowRef = await db.collection('workflows').add(newWorkflowData);
    return { id: newWorkflowRef.id, ...newWorkflowData };
  } catch (error) {
    console.error('Error duplicating workflow:', error);
    throw error;
  }
};
